import { useCallback, useContext, useMemo } from 'react';
import { giftcardState } from '../Pages/Giftcard/client-state/giftcard-state-context';
import { getDurationString } from '../Utils/utils';
import useGiftcardShop from '../Pages/Giftcard/server-data/use-giftcard-shop';

const useGiftcardPrices = () => {
  const { data: shop } = useGiftcardShop();
  const { state } = useContext(giftcardState);

  const priceModel = useMemo(() => {
    return shop?.priceModels.find((priceModel) => priceModel.giftcards);
  }, [shop?.priceModels]);

  const calculateGiftcardPrices = useCallback(() => {
    const prices = {
      rentalPrice: 0,
      voucherValue: 0,
      sailingPrice: 0,
      extrasPrice: 0,
      paymentValue: 0,
      fullPrice: 0,
      balance: 0,
      toBePaid: 0,
      toBeRefunded: 0,
    };

    if (priceModel?.durations) {
      if (state.customAmount) {
        prices.rentalPrice = state.customAmount * 100;
      } else {
        const durationString = getDurationString(state.giftcard.duration);
        const duration = priceModel.durations.find((d) => d.duration === durationString);
        const durationPrice = duration?.price ?? 0;

        prices.rentalPrice = durationPrice;
      }

      if (state.vouchers && state.vouchers.length > 0) {
        for (const voucher of state.vouchers) {
          prices.rentalPrice = voucher.changes?.giftcard?.value ?? prices.rentalPrice;

          if (voucher.type === 'percent') {
            prices.voucherValue += (prices.rentalPrice / 100) * voucher.value;
          } else {
            prices.voucherValue += voucher.value;
          }
        }
      }

      prices.sailingPrice = prices.rentalPrice - prices.voucherValue;
      if (prices.sailingPrice < 0) prices.sailingPrice = 0;

      prices.fullPrice = prices.sailingPrice + prices.extrasPrice;
      prices.balance = prices.paymentValue - prices.fullPrice;
      // If the balance is negative (customer needs to pay),
      // turn the negative balance in to a positive toBePaid value.
      // If the balance is positive, (customer have paid to much),
      // set the toBePaid value to 0.
      prices.toBePaid = prices.balance < 0 ? Math.abs(prices.balance) : 0;
      // If the balance is negative (customer needs to pay),
      // set the toBeRefunded to 0.
      // If the balance is positive, (customer have paid to much),
      // set the toBeRefunded to the balance.
      prices.toBeRefunded = prices.balance > 0 ? prices.balance : 0;

      return prices;
    }

    return {
      rentalPrice: undefined,
      voucherValue: undefined,
      sailingPrice: undefined,
      extrasPrice: undefined,
      paymentValue: undefined,
      fullPrice: undefined,
      balance: undefined,
      toBePaid: undefined,
      toBeRefunded: undefined,
    };
  }, [
    priceModel?.durations,
    state.giftcard.duration,
    state.vouchers,
    state.customAmount,
  ]);

  const giftcardPrices = useMemo(
    () => calculateGiftcardPrices(),
    [calculateGiftcardPrices]
  );

  return { giftcardPrices, calculateGiftcardPrices };
};

export default useGiftcardPrices;
